import React, { useState, useEffect } from "react";
import { X, Download, ChevronDown, ChevronUp, Edit2, Trash2, Send, CheckCircle2 } from "lucide-react";
import { doc, getDoc, updateDoc, collection, getDocs, query, where, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import jsPDF from "jspdf";
import toast from "react-hot-toast";
import { ContactChangeModal, DueDateModal, CapturePaymentModal, CreditNoteModal, SendInvoiceModal, ViewPDFModal } from "./Invoice/InvoiceModals";
import { TimelineDetailModal } from "./Invoice/TimelineDetailModal";
import { AttachExpensesModal } from "./Invoice/AttachExpensesModal";
import { PaymentsTab, CreditNotesTab, NotesTab, TimelineTab } from "./Invoice/InvoiceTabs";
import { reSyncInvoiceToClient } from "../services/invoiceSyncService";

function InvoiceDetailsView({ isOpen, onClose, invoice, globalUser, onPaymentCapture, onEdit, onDelete, readOnly = false }) {
  const [lawyerDetails, setLawyerDetails] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("payments");
  const [showParticulars, setShowParticulars] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [showCapturePaymentModal, setShowCapturePaymentModal] = useState(false);
  const [showSendInvoiceModal, setShowSendInvoiceModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showTimelineDetailModal, setShowTimelineDetailModal] = useState(false);
  const [selectedTimelineEvent, setSelectedTimelineEvent] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(invoice?.contactId || "");
  const [newDueDate, setNewDueDate] = useState(invoice?.dueDate || "");
  const [newNoteText, setNewNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [showAttachExpensesModal, setShowAttachExpensesModal] = useState(false);
  const [caseExpenses, setCaseExpenses] = useState([]);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [customExpenses, setCustomExpenses] = useState([{ id: Date.now(), description: "", amount: "" }]);
  const [attachingExpenses, setAttachingExpenses] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showViewPdfModal, setShowViewPdfModal] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState("");
  const [paymentData, setPaymentData] = useState({
    amount: "",
    date: new Date().toISOString().split("T")[0],
    mode: "Cash",
    reference: "",
    notes: "",
    tds: ""
  });
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [editingCreditNote, setEditingCreditNote] = useState(null);
  const [creditNoteData, setCreditNoteData] = useState({
    amount: "",
    date: new Date().toISOString().split("T")[0],
    reason: "",
    reference: "",
    notes: ""
  });
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    if (isOpen && invoice && globalUser) {
      fetchDetails();
      fetchAllClients();
      setSelectedContactId(invoice.contactId || "");
      setNewDueDate(invoice.dueDate || "");
      setPaymentData({
        amount: "",
        date: new Date().toISOString().split("T")[0],
        mode: "Cash",
        reference: "",
        notes: "",
        tds: ""
      });
      setCreditNoteData({
        amount: "",
        date: new Date().toISOString().split("T")[0],
        reason: "",
        reference: "",
        notes: ""
      });
    }
  }, [isOpen, invoice, globalUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsDropdown && !event.target.closest('.relative')) {
        setShowActionsDropdown(false);
      }
    };

    if (showActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsDropdown]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      if (readOnly && invoice.sharedBy) {
        // Client mode: fetch lawyer details from sharedBy uid
        const lawyerDoc = await getDoc(doc(db, "lawyers", invoice.sharedBy));
        if (lawyerDoc.exists()) {
          setLawyerDetails(lawyerDoc.data());
        }
        // In client mode, the client IS the contact, so set basic details from invoice
        if (invoice.contactName) {
          setClientDetails({ name: invoice.contactName });
        }
      } else {
        // Lawyer mode
        const lawyerDoc = await getDoc(doc(db, "lawyers", globalUser.uid));
        if (lawyerDoc.exists()) {
          setLawyerDetails(lawyerDoc.data());
        }

        // Fetch client details
        if (invoice.contactId) {
          const clientDoc = await getDoc(doc(db, "clients", invoice.contactId));
          if (clientDoc.exists()) {
            setClientDetails(clientDoc.data());
          }
        }
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      toast.error("Failed to load invoice details");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllClients = async () => {
    try {
      const clientsQuery = query(
        collection(db, "clients"),
        where("owner", "==", globalUser.uid)
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsList = clientsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllClients(clientsList);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    }
  };

  // Helper function to add timeline events
  const addTimelineEvent = async (eventType, changes = {}) => {
    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);

      const timelineEvent = {
        id: Date.now().toString(),
        type: eventType,
        timestamp: new Date().toISOString(),
        by: globalUser?.displayName || globalUser?.email || "Unknown User",
        changes: changes
      };

      const currentTimeline = invoice.timeline || [];
      await updateDoc(invoiceRef, {
        timeline: arrayUnion(timelineEvent)
      });

      // Update local state
      invoice.timeline = [...currentTimeline, timelineEvent];
    } catch (error) {
      console.error("Error adding timeline event:", error);
    }
  };

  // Re-sync the full invoice to the client's portal after any update
  const syncToClient = async () => {
    if (readOnly || !invoice?.contactId || !globalUser?.uid) return;
    const lawyerName = lawyerDetails?.name || globalUser?.displayName || "Unknown";
    await reSyncInvoiceToClient(globalUser.uid, invoice.id, invoice.contactId, lawyerName);
  };

  const [markingPaid, setMarkingPaid] = useState(false);

  const handleMarkPaid = async () => {
    if (invoice.status === "paid") return;

    setMarkingPaid(true);
    try {
      const now = new Date().toISOString();
      const userName = globalUser?.displayName || globalUser?.email || "User";
      const isClientUser = readOnly;

      const timelineEvent = {
        id: Date.now().toString(),
        type: "marked_as_paid",
        timestamp: now,
        by: userName,
        changes: {
          "Invoice Status": { old: invoice.status, new: "paid" },
          "Marked By": { old: "-", new: isClientUser ? "Client" : "Lawyer" }
        }
      };

      if (isClientUser) {
        const sharedInvoiceRef = doc(db, "client", globalUser.uid, "sharedInvoices", invoice.id);
        await updateDoc(sharedInvoiceRef, {
          status: "paid",
          dueAmount: 0,
          markedPaidAt: now,
          markedPaidBy: "client",
          timeline: arrayUnion(timelineEvent)
        });
      } else {
        const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);
        await updateDoc(invoiceRef, {
          status: "paid",
          dueAmount: 0,
          markedPaidAt: now,
          markedPaidBy: "lawyer",
          timeline: arrayUnion(timelineEvent)
        });
        await syncToClient();
      }

      // Update local state
      invoice.status = "paid";
      invoice.dueAmount = 0;

      toast.success("Invoice marked as paid");

      if (onPaymentCapture) {
        onPaymentCapture();
      }
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error("Failed to mark as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleMarkUnpaid = async () => {
    if (invoice.status !== "paid") return;

    setMarkingPaid(true);
    try {
      const now = new Date().toISOString();
      const userName = globalUser?.displayName || globalUser?.email || "User";
      const isClientUser = readOnly;

      const timelineEvent = {
        id: Date.now().toString(),
        type: "marked_as_unpaid",
        timestamp: now,
        by: userName,
        changes: {
          "Invoice Status": { old: "paid", new: "raised" },
          "Marked By": { old: "-", new: isClientUser ? "Client" : "Lawyer" }
        }
      };

      if (isClientUser) {
        const sharedInvoiceRef = doc(db, "client", globalUser.uid, "sharedInvoices", invoice.id);
        await updateDoc(sharedInvoiceRef, {
          status: "raised",
          dueAmount: invoice.totalAmount || 0,
          markedPaidAt: null,
          markedPaidBy: null,
          timeline: arrayUnion(timelineEvent)
        });
      } else {
        const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);
        await updateDoc(invoiceRef, {
          status: "raised",
          dueAmount: invoice.totalAmount || 0,
          markedPaidAt: null,
          markedPaidBy: null,
          timeline: arrayUnion(timelineEvent)
        });
        await syncToClient();
      }

      // Update local state
      invoice.status = "raised";
      invoice.dueAmount = invoice.totalAmount || 0;

      toast.success("Invoice marked as unpaid");

      if (onPaymentCapture) {
        onPaymentCapture();
      }
    } catch (error) {
      console.error("Error marking invoice as unpaid:", error);
      toast.error("Failed to mark as unpaid");
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setSavingNote(true);
    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);

      const note = {
        id: Date.now().toString(),
        text: newNoteText.trim(),
        createdBy: globalUser?.displayName || globalUser?.email || "Unknown User",
        createdAt: new Date().toISOString()
      };

      await updateDoc(invoiceRef, {
        notes: arrayUnion(note)
      });

      // Add timeline event
      await addTimelineEvent("note_added", {
        "Note": {
          old: "-",
          new: newNoteText.trim().substring(0, 100) + (newNoteText.trim().length > 100 ? "..." : "")
        }
      });

      // Update local state
      invoice.notes = invoice.notes ? [...invoice.notes, note] : [note];

      toast.success("Note added successfully");
      setNewNoteText("");
      await syncToClient();
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleOpenAttachExpensesModal = () => {
    // Load case expenses if this is a case-based invoice
    if (invoice.selectedCase && invoice.selectedCase.expenses) {
      setCaseExpenses(invoice.selectedCase.expenses);
      setSelectedExpenses(invoice.attachedExpenses || []);
    } else {
      setCaseExpenses([]);
      setSelectedExpenses([]);
    }
    setCustomExpenses([{ id: Date.now(), description: "", amount: "" }]);
    setShowAttachExpensesModal(true);
  };

  const handleToggleExpense = (expenseId) => {
    if (selectedExpenses.includes(expenseId)) {
      setSelectedExpenses(selectedExpenses.filter(id => id !== expenseId));
    } else {
      setSelectedExpenses([...selectedExpenses, expenseId]);
    }
  };

  const addCustomExpense = () => {
    setCustomExpenses([...customExpenses, { id: Date.now(), description: "", amount: "" }]);
  };

  const removeCustomExpense = (id) => {
    if (customExpenses.length === 1) {
      toast.error("At least one expense row is required");
      return;
    }
    setCustomExpenses(customExpenses.filter(exp => exp.id !== id));
  };

  const updateCustomExpense = (id, field, value) => {
    setCustomExpenses(customExpenses.map(exp =>
      exp.id === id ? { ...exp, [field]: value } : exp
    ));
  };

  const handleAttachExpenses = async () => {
    setAttachingExpenses(true);
    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);

      let attachedExpensesList = [];
      let totalExpenseAmount = 0;

      // Handle case expenses
      if (caseExpenses.length > 0 && selectedExpenses.length > 0) {
        selectedExpenses.forEach(expId => {
          const expense = caseExpenses.find(e => e.id === expId);
          if (expense) {
            attachedExpensesList.push(expense);
            totalExpenseAmount += parseFloat(expense.amount || 0);
          }
        });
      }

      // Handle custom expenses
      const validCustomExpenses = customExpenses.filter(exp => exp.description.trim() && exp.amount);
      if (validCustomExpenses.length > 0) {
        validCustomExpenses.forEach(exp => {
          attachedExpensesList.push({
            id: exp.id.toString(),
            description: exp.description.trim(),
            amount: parseFloat(exp.amount),
            type: "Custom Expense",
            date: new Date().toISOString().split("T")[0]
          });
          totalExpenseAmount += parseFloat(exp.amount);
        });
      }

      if (attachedExpensesList.length === 0) {
        toast.error("Please select case expenses or add custom expenses");
        setAttachingExpenses(false);
        return;
      }

      // Calculate new total
      const currentTotal = parseFloat(invoice.totalAmount || 0);
      const newTotal = currentTotal + totalExpenseAmount;

      await updateDoc(invoiceRef, {
        attachedExpenses: attachedExpensesList,
        totalAmount: newTotal,
        dueAmount: parseFloat(invoice.dueAmount || 0) + totalExpenseAmount
      });

      // Add timeline event
      await addTimelineEvent("expenses_attached", {
        "Expenses Added": {
          old: "-",
          new: `${attachedExpensesList.length} expense(s)`
        },
        "Total Amount": {
          old: formatAmount(currentTotal, invoice.currency),
          new: formatAmount(newTotal, invoice.currency)
        }
      });

      // Update local state
      invoice.attachedExpenses = attachedExpensesList;
      invoice.totalAmount = newTotal;
      invoice.dueAmount = parseFloat(invoice.dueAmount || 0) + totalExpenseAmount;

      toast.success(`${attachedExpensesList.length} expense(s) attached successfully`);
      setShowAttachExpensesModal(false);
      await syncToClient();
    } catch (error) {
      console.error("Error attaching expenses:", error);
      toast.error("Failed to attach expenses");
    } finally {
      setAttachingExpenses(false);
    }
  };

  const handleContactChange = async () => {
    if (!selectedContactId) {
      toast.error("Please select a contact");
      return;
    }

    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);
      const selectedClient = allClients.find((c) => c.id === selectedContactId);
      const contactName = selectedClient.salutation
        ? `${selectedClient.salutation} ${selectedClient.name}`
        : selectedClient.name;

      // Track old value for timeline
      const oldContactName = invoice.contactName;

      await updateDoc(invoiceRef, {
        contactId: selectedContactId,
        contactName: contactName,
      });

      // Add timeline event
      await addTimelineEvent("contact_updated", {
        "Contact Name": {
          old: oldContactName,
          new: contactName
        }
      });

      // Update local state
      invoice.contactId = selectedContactId;
      invoice.contactName = contactName;

      // Refresh client details
      const clientDoc = await getDoc(doc(db, "clients", selectedContactId));
      if (clientDoc.exists()) {
        setClientDetails(clientDoc.data());
      }

      toast.success("Contact updated successfully");
      setShowContactModal(false);
      await syncToClient();
    } catch (error) {
      console.error("Error updating contact:", error);
      toast.error("Failed to update contact");
    }
  };

  const handleDueDateChange = async () => {
    if (!newDueDate) {
      toast.error("Please select a due date");
      return;
    }

    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);

      // Track old value for timeline
      const oldDueDate = invoice.dueDate;

      await updateDoc(invoiceRef, {
        dueDate: newDueDate,
      });

      // Add timeline event
      await addTimelineEvent("due_date_updated", {
        "Due Date": {
          old: formatDate(oldDueDate),
          new: formatDate(newDueDate)
        }
      });

      // Update local state
      invoice.dueDate = newDueDate;

      toast.success("Due date updated successfully");
      setShowDueDateModal(false);
      await syncToClient();
    } catch (error) {
      console.error("Error updating due date:", error);
      toast.error("Failed to update due date");
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentData({
      amount: payment.amount.toString(),
      date: payment.date,
      mode: payment.mode,
      reference: payment.reference || "",
      notes: payment.notes || "",
      tds: (payment.tds || 0).toString()
    });
    setShowCapturePaymentModal(true);
  };

  const handleDeletePayment = async (paymentToDelete) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) {
      return;
    }

    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);

      // Remove the payment from the payments array
      const updatedPayments = invoice.payments.filter(p => p.id !== paymentToDelete.id);

      // Recalculate total paid and due amount
      const totalPaid = updatedPayments.reduce((sum, p) =>
        sum + parseFloat(p.amount) + (parseFloat(p.tds) || 0), 0
      );
      const newDueAmount = parseFloat(invoice.totalAmount) - totalPaid;
      const oldStatus = invoice.status;
      const newStatus = newDueAmount <= 0 ? "paid" : "raised";

      // Update invoice in Firestore
      await updateDoc(invoiceRef, {
        payments: updatedPayments,
        dueAmount: Math.max(0, newDueAmount),
        status: newStatus
      });

      // Add timeline event
      await addTimelineEvent("payment_deleted", {
        "Payment Amount": {
          old: formatAmount(paymentToDelete.amount, invoice.currency),
          new: "-"
        },
        "Payment Date": {
          old: formatDate(paymentToDelete.date),
          new: "-"
        },
        ...(oldStatus !== newStatus && {
          "Invoice Status": {
            old: oldStatus,
            new: newStatus
          }
        })
      });

      // Update local state
      invoice.payments = updatedPayments;
      invoice.dueAmount = Math.max(0, newDueAmount);
      invoice.status = newStatus;

      toast.success("Payment deleted successfully");
      await syncToClient();

      // Call callback to refresh parent component
      if (onPaymentCapture) {
        onPaymentCapture();
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Failed to delete payment");
    }
  };

  const handleCapturePayment = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    if (!paymentData.date) {
      toast.error("Please select a payment date");
      return;
    }

    const paymentAmount = parseFloat(paymentData.amount);
    const tdsAmount = parseFloat(paymentData.tds) || 0;
    const totalPaidNow = paymentAmount + tdsAmount;

    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);

      if (editingPayment) {
        // EDIT MODE: Update existing payment
        const oldStatus = invoice.status;

        const updatedPayments = invoice.payments.map(p =>
          p.id === editingPayment.id
            ? {
                ...p,
                amount: paymentAmount,
                tds: tdsAmount,
                date: paymentData.date,
                mode: paymentData.mode,
                reference: paymentData.reference,
                notes: paymentData.notes,
                updatedAt: new Date().toISOString()
              }
            : p
        );

        // Recalculate total paid and due amount
        const totalPaid = updatedPayments.reduce((sum, p) =>
          sum + parseFloat(p.amount) + (parseFloat(p.tds) || 0), 0
        );
        const newDueAmount = parseFloat(invoice.totalAmount) - totalPaid;
        const newStatus = newDueAmount <= 0 ? "paid" : "raised";

        // Update invoice in Firestore
        await updateDoc(invoiceRef, {
          payments: updatedPayments,
          dueAmount: Math.max(0, newDueAmount),
          status: newStatus
        });

        // Add timeline event for payment edit
        const changes = {};
        if (editingPayment.amount !== paymentAmount) {
          changes["Payment Amount"] = {
            old: formatAmount(editingPayment.amount, invoice.currency),
            new: formatAmount(paymentAmount, invoice.currency)
          };
        }
        if (editingPayment.tds !== tdsAmount && tdsAmount > 0) {
          changes["TDS Amount"] = {
            old: formatAmount(editingPayment.tds || 0, invoice.currency),
            new: formatAmount(tdsAmount, invoice.currency)
          };
        }
        if (editingPayment.date !== paymentData.date) {
          changes["Payment Date"] = {
            old: formatDate(editingPayment.date),
            new: formatDate(paymentData.date)
          };
        }
        if (oldStatus !== newStatus) {
          changes["Invoice Status"] = {
            old: oldStatus,
            new: newStatus
          };
        }

        await addTimelineEvent("payment_updated", changes);

        // Update local state
        invoice.payments = updatedPayments;
        invoice.dueAmount = Math.max(0, newDueAmount);
        invoice.status = newStatus;

        toast.success("Payment updated successfully!");
        await syncToClient();
      } else {
        // CREATE MODE: Add new payment
        const currentDueAmount = parseFloat(invoice.dueAmount || invoice.totalAmount);

        if (totalPaidNow > currentDueAmount) {
          toast.error(`Payment amount (${paymentAmount} + TDS ${tdsAmount}) cannot exceed due amount (${currentDueAmount})`);
          return;
        }

        // Create payment record
        const payment = {
          id: Date.now().toString(),
          amount: paymentAmount,
          tds: tdsAmount,
          date: paymentData.date,
          mode: paymentData.mode,
          reference: paymentData.reference,
          notes: paymentData.notes,
          capturedAt: new Date().toISOString()
        };

        // Calculate new due amount
        const totalPaid = paymentAmount + tdsAmount;
        const newDueAmount = currentDueAmount - totalPaid;
        const oldStatus = invoice.status;
        const newStatus = newDueAmount <= 0 ? "paid" : "raised";

        // Update invoice in Firestore
        await updateDoc(invoiceRef, {
          payments: arrayUnion(payment),
          dueAmount: Math.max(0, newDueAmount),
          status: newStatus,
          lastPaymentDate: paymentData.date
        });

        // Add timeline event for payment capture
        const changes = {
          "Payment Amount": {
            old: "-",
            new: formatAmount(paymentAmount, invoice.currency)
          },
          "Payment Date": {
            old: "-",
            new: formatDate(paymentData.date)
          },
          "Payment Mode": {
            old: "-",
            new: paymentData.mode
          }
        };
        if (tdsAmount > 0) {
          changes["TDS Amount"] = {
            old: "-",
            new: formatAmount(tdsAmount, invoice.currency)
          };
        }
        if (oldStatus !== newStatus) {
          changes["Invoice Status"] = {
            old: oldStatus,
            new: newStatus
          };
        }

        await addTimelineEvent("payment_captured", changes);

        // Update local state
        invoice.payments = invoice.payments ? [...invoice.payments, payment] : [payment];
        invoice.dueAmount = Math.max(0, newDueAmount);
        invoice.status = newStatus;
        invoice.lastPaymentDate = paymentData.date;

        toast.success(`Payment captured successfully! Invoice status: ${newStatus}`);
        await syncToClient();
      }

      setShowCapturePaymentModal(false);
      setEditingPayment(null);

      // Call callback to refresh parent component
      if (onPaymentCapture) {
        onPaymentCapture();
      }

      // Reset payment form
      setPaymentData({
        amount: "",
        date: new Date().toISOString().split("T")[0],
        mode: "Cash",
        reference: "",
        notes: "",
        tds: ""
      });
    } catch (error) {
      console.error("Error with payment:", error);
      toast.error(editingPayment ? "Failed to update payment" : "Failed to capture payment");
    }
  };

  const handleAddCreditNote = async () => {
    if (!creditNoteData.amount || parseFloat(creditNoteData.amount) <= 0) {
      toast.error("Please enter a valid credit note amount");
      return;
    }

    if (!creditNoteData.date) {
      toast.error("Please select a credit note date");
      return;
    }

    if (!creditNoteData.reason || creditNoteData.reason.trim() === "") {
      toast.error("Please enter a reason for the credit note");
      return;
    }

    const creditAmount = parseFloat(creditNoteData.amount);

    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);

      if (editingCreditNote) {
        // EDIT MODE: Update existing credit note
        const oldStatus = invoice.status;

        const updatedCreditNotes = invoice.creditNotes.map(cn =>
          cn.id === editingCreditNote.id
            ? {
                ...cn,
                amount: creditAmount,
                date: creditNoteData.date,
                reason: creditNoteData.reason,
                reference: creditNoteData.reference,
                notes: creditNoteData.notes,
                updatedAt: new Date().toISOString()
              }
            : cn
        );

        // Recalculate total credit notes
        const totalCreditNotes = updatedCreditNotes.reduce((sum, cn) =>
          sum + parseFloat(cn.amount), 0
        );

        // Calculate total paid from payments
        const totalPaid = (invoice.payments || []).reduce((sum, p) =>
          sum + parseFloat(p.amount) + (parseFloat(p.tds) || 0), 0
        );

        // New due amount = Total - Payments - Credit Notes
        const newDueAmount = parseFloat(invoice.totalAmount) - totalPaid - totalCreditNotes;
        const newStatus = newDueAmount <= 0 ? "paid" : "raised";

        // Update invoice in Firestore
        await updateDoc(invoiceRef, {
          creditNotes: updatedCreditNotes,
          dueAmount: Math.max(0, newDueAmount),
          status: newStatus
        });

        // Add timeline event for credit note edit
        const changes = {};
        if (editingCreditNote.amount !== creditAmount) {
          changes["Credit Note Amount"] = {
            old: formatAmount(editingCreditNote.amount, invoice.currency),
            new: formatAmount(creditAmount, invoice.currency)
          };
        }
        if (editingCreditNote.reason !== creditNoteData.reason) {
          changes["Reason"] = {
            old: editingCreditNote.reason,
            new: creditNoteData.reason
          };
        }
        if (editingCreditNote.date !== creditNoteData.date) {
          changes["Credit Note Date"] = {
            old: formatDate(editingCreditNote.date),
            new: formatDate(creditNoteData.date)
          };
        }
        if (oldStatus !== newStatus) {
          changes["Invoice Status"] = {
            old: oldStatus,
            new: newStatus
          };
        }

        await addTimelineEvent("credit_note_updated", changes);

        // Update local state
        invoice.creditNotes = updatedCreditNotes;
        invoice.dueAmount = Math.max(0, newDueAmount);
        invoice.status = newStatus;

        toast.success("Credit note updated successfully!");
        await syncToClient();
      } else {
        // CREATE MODE: Add new credit note
        const currentDueAmount = parseFloat(invoice.dueAmount || invoice.totalAmount);

        if (creditAmount > currentDueAmount) {
          toast.error(`Credit note amount cannot exceed due amount (${formatAmount(currentDueAmount, invoice.currency)})`);
          return;
        }

        // Create credit note record
        const creditNote = {
          id: Date.now().toString(),
          amount: creditAmount,
          date: creditNoteData.date,
          reason: creditNoteData.reason,
          reference: creditNoteData.reference,
          notes: creditNoteData.notes,
          createdAt: new Date().toISOString()
        };

        // Calculate new due amount
        const newDueAmount = currentDueAmount - creditAmount;
        const oldStatus = invoice.status;
        const newStatus = newDueAmount <= 0 ? "paid" : "raised";

        // Update invoice in Firestore
        await updateDoc(invoiceRef, {
          creditNotes: arrayUnion(creditNote),
          dueAmount: Math.max(0, newDueAmount),
          status: newStatus
        });

        // Add timeline event for credit note creation
        const changes = {
          "Credit Note Amount": {
            old: "-",
            new: formatAmount(creditAmount, invoice.currency)
          },
          "Credit Note Date": {
            old: "-",
            new: formatDate(creditNoteData.date)
          },
          "Reason": {
            old: "-",
            new: creditNoteData.reason
          }
        };
        if (oldStatus !== newStatus) {
          changes["Invoice Status"] = {
            old: oldStatus,
            new: newStatus
          };
        }

        await addTimelineEvent("credit_note_added", changes);

        // Update local state
        invoice.creditNotes = invoice.creditNotes ? [...invoice.creditNotes, creditNote] : [creditNote];
        invoice.dueAmount = Math.max(0, newDueAmount);
        invoice.status = newStatus;

        toast.success(`Credit note added successfully! New due amount: ${formatAmount(newDueAmount, invoice.currency)}`);
        await syncToClient();
      }

      setShowCreditNoteModal(false);
      setEditingCreditNote(null);

      // Call callback to refresh parent component
      if (onPaymentCapture) {
        onPaymentCapture();
      }

      // Reset credit note form
      setCreditNoteData({
        amount: "",
        date: new Date().toISOString().split("T")[0],
        reason: "",
        reference: "",
        notes: ""
      });
    } catch (error) {
      console.error("Error with credit note:", error);
      toast.error(editingCreditNote ? "Failed to update credit note" : "Failed to add credit note");
    }
  };

  const handleDeleteCreditNote = async (creditNoteId) => {
    if (!window.confirm("Are you sure you want to delete this credit note?")) {
      return;
    }

    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);
      const creditNote = invoice.creditNotes.find(cn => cn.id === creditNoteId);

      if (!creditNote) {
        toast.error("Credit note not found");
        return;
      }

      const oldStatus = invoice.status;

      // Remove credit note from array
      const updatedCreditNotes = invoice.creditNotes.filter(cn => cn.id !== creditNoteId);

      // Recalculate due amount
      const totalCreditNotes = updatedCreditNotes.reduce((sum, cn) =>
        sum + parseFloat(cn.amount), 0
      );
      const totalPaid = (invoice.payments || []).reduce((sum, p) =>
        sum + parseFloat(p.amount) + (parseFloat(p.tds) || 0), 0
      );
      const newDueAmount = parseFloat(invoice.totalAmount) - totalPaid - totalCreditNotes;
      const newStatus = newDueAmount <= 0 ? "paid" : "raised";

      // Update Firestore
      await updateDoc(invoiceRef, {
        creditNotes: updatedCreditNotes,
        dueAmount: Math.max(0, newDueAmount),
        status: newStatus
      });

      // Add timeline event
      const changes = {
        "Credit Note Amount": {
          old: formatAmount(creditNote.amount, invoice.currency),
          new: "-"
        },
        "Reason": {
          old: creditNote.reason,
          new: "Deleted"
        }
      };
      if (oldStatus !== newStatus) {
        changes["Invoice Status"] = {
          old: oldStatus,
          new: newStatus
        };
      }

      await addTimelineEvent("credit_note_deleted", changes);

      // Update local state
      invoice.creditNotes = updatedCreditNotes;
      invoice.dueAmount = Math.max(0, newDueAmount);
      invoice.status = newStatus;

      toast.success("Credit note deleted successfully");
      await syncToClient();

      if (onPaymentCapture) {
        onPaymentCapture();
      }
    } catch (error) {
      console.error("Error deleting credit note:", error);
      toast.error("Failed to delete credit note");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "INR":
      default:
        return "₹";
    }
  };

  // For PDF generation - use text that displays correctly in PDF fonts
  const getCurrencySymbolForPDF = (currency) => {
    switch (currency) {
      case "USD":
        return "$";
      case "EUR":
        return "EUR";
      case "INR":
      default:
        return "Rs.";  // Use "Rs." instead of ₹ symbol for PDF compatibility
    }
  };

  const formatAmount = (amount, currency = "INR") => {
    const symbol = getCurrencySymbol(currency);
    const numValue = parseFloat(amount || 0);
    // Format with thousand separators
    const formattedValue = numValue.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `${symbol} ${formattedValue}`;
  };

  // Simplified format for PDF to avoid encoding issues
  const formatAmountForPDF = (amount, currency = "INR") => {
    const symbol = getCurrencySymbolForPDF(currency);  // Use PDF-compatible symbol
    const numValue = parseFloat(amount || 0);
    // Use simple number formatting without locale
    const formattedValue = numValue.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${symbol} ${formattedValue}`;
  };

  const formatAddress = (address) => {
    if (!address) return "";
    const parts = [];
    if (address.building) parts.push(address.building);
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.district) parts.push(address.district);
    if (address.state) parts.push(address.state);
    if (address.pincode) parts.push(address.pincode);
    if (address.country) parts.push(address.country);
    return parts.join(", ");
  };

  const generatePDF = () => {
    console.log("=== Generating PDF ===");
    console.log("Invoice Number from database:", invoice.invoiceNumber);
    console.log("Invoice Total Amount:", invoice.totalAmount);
    console.log("Invoice Currency:", invoice.currency);

    const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = 25;

      // Case name at top left (if available) - Bold, larger font
      if (invoice.resourceType === "case" && invoice.resourceName) {
        pdf.setFontSize(14);
        pdf.setFont("times", "bold");
        const caseName = invoice.resourceName;
        const splitCaseName = pdf.splitTextToSize(caseName, 100);
        pdf.text(splitCaseName, margin, yPosition);
        yPosition += splitCaseName.length * 7;
      } else {
        yPosition += 5;
      }

      // "Invoice" title at top right - Large, bold
      pdf.setFontSize(32);
      pdf.setFont("times", "bold");
      pdf.text("Invoice", pageWidth - margin, 25, { align: "right" });

      yPosition += 10;

      // Lawyer Details on left side
      const detailsStartY = yPosition;
      pdf.setFontSize(11);
      pdf.setFont("times", "bold");
      if (lawyerDetails?.name) {
        pdf.text(lawyerDetails.name, margin, yPosition);
        yPosition += 6;
      }

      pdf.setFont("times", "normal");
      if (lawyerDetails?.phoneNumber) {
        pdf.text(`Phone: ${lawyerDetails.phoneNumber}`, margin, yPosition);
        yPosition += 6;
      }
      if (lawyerDetails?.email) {
        pdf.text(`Email: ${lawyerDetails.email}`, margin, yPosition);
        yPosition += 6;
      }

      // Invoice Number and Date on right side (aligned with lawyer details)
      let rightY = detailsStartY;
      pdf.setFont("times", "normal");
      pdf.setFontSize(11);
      pdf.text(`Invoice #: ${invoice.invoiceNumber}`, pageWidth - margin, rightY, { align: "right" });
      rightY += 6;

      const invoiceDate = formatDate(invoice.raisedOn || new Date());
      pdf.text(`Date: ${invoiceDate}`, pageWidth - margin, rightY, { align: "right" });

      yPosition += 15;

      // Bill To Section - Dark header
      pdf.setFillColor(51, 51, 51);
      pdf.rect(margin, yPosition, pageWidth - 2 * margin, 10, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("times", "bold");
      pdf.setFontSize(12);
      pdf.text("Bill To", margin + 3, yPosition + 7);
      pdf.setTextColor(0, 0, 0);
      yPosition += 15;

      // Client Details
      pdf.setFont("times", "normal");
      pdf.setFontSize(11);
      if (clientDetails) {
        const clientName = clientDetails.salutation
          ? `${clientDetails.salutation} ${clientDetails.name}`
          : clientDetails.name;
        pdf.text(clientName, margin, yPosition);
        yPosition += 6;

        if (clientDetails.email) {
          pdf.text(`Email: ${clientDetails.email}`, margin, yPosition);
          yPosition += 6;
        }
      } else if (invoice.contactName) {
        pdf.text(invoice.contactName, margin, yPosition);
        yPosition += 6;
      }

      yPosition += 10;

      // Particulars Table Header - Dark background
      const amountColumnX = pageWidth - margin - 10; // Fixed x position for amount column
      pdf.setFillColor(51, 51, 51);
      pdf.rect(margin, yPosition, pageWidth - 2 * margin, 10, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("times", "bold");
      pdf.setFontSize(12);
      pdf.text("Particulars", margin + 3, yPosition + 7);
      pdf.text("Amount", amountColumnX, yPosition + 7, { align: "right" });
      pdf.setTextColor(0, 0, 0);
      yPosition += 15;

      // Particulars Items
      pdf.setFont("times", "normal");
      pdf.setFontSize(11);

      if (invoice.particulars && invoice.particulars.length > 0) {
        invoice.particulars.forEach((particular) => {
          pdf.text(particular.description, margin + 3, yPosition);
          const amountText = formatAmountForPDF(particular.amount, invoice.currency);
          pdf.text(amountText, amountColumnX, yPosition, { align: "right" });
          yPosition += 7;
        });
      }

      // Selected Expenses
      if (invoice.selectedExpenses && invoice.selectedExpenses.length > 0 && invoice.selectedCase) {
        const expenses = invoice.selectedCase.expenses || [];
        invoice.selectedExpenses.forEach((expenseId) => {
          const expense = expenses.find((e) => e.id === expenseId);
          if (expense) {
            const description = expense.description || expense.type;
            pdf.text(description, margin + 3, yPosition);
            const amountText = formatAmountForPDF(expense.amount, invoice.currency);
            pdf.text(amountText, amountColumnX, yPosition, { align: "right" });
            yPosition += 7;
          }
        });
      }

      // Draw line before total
      yPosition += 2;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(pageWidth - margin - 60, yPosition, amountColumnX, yPosition);
      yPosition += 8;

      // Total - Bold with label on left, amount right-aligned
      pdf.setFont("times", "bold");
      pdf.setFontSize(12);

      // Draw "Total:" label
      const totalLabelX = pageWidth - margin - 60;
      pdf.text("Total:", totalLabelX, yPosition);

      // Draw amount aligned with column
      const totalAmountText = formatAmountForPDF(invoice.totalAmount, invoice.currency);
      pdf.text(totalAmountText, amountColumnX, yPosition, { align: "right" });

      return pdf;
  };

  const handleDownloadPDF = () => {
    try {
      const pdf = generatePDF();
      const fileName = `${invoice.invoiceNumber.replace(/[\/\\:*?"<>|]/g, "_")}.pdf`;
      pdf.save(fileName);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handleViewPDF = () => {
    try {
      const pdf = generatePDF();
      const pdfUrl = pdf.output('dataurlstring');
      setPdfDataUrl(pdfUrl);
      setShowViewPdfModal(true);
      setShowActionsDropdown(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handleEditInvoice = () => {
    setShowActionsDropdown(false);
    if (onEdit) {
      onEdit(invoice);
    } else {
      toast.error("Edit functionality not available");
    }
  };

  const handleDeleteInvoice = () => {
    setShowActionsDropdown(false);
    if (window.confirm(`Are you sure you want to delete invoice ${invoice.invoiceNumber}? This action cannot be undone.`)) {
      if (onDelete) {
        onDelete(invoice.id);
        onClose();
      } else {
        toast.error("Delete functionality not available");
      }
    }
  };

  const handleSendInvoiceClick = () => {
    // Check if client has email first
    if (!clientDetails?.email) {
      // Show Add Email modal first
      setShowAddEmailModal(true);
    } else {
      // Client has email, proceed to send invoice confirmation
      setShowSendInvoiceModal(true);
    }
  };

  const handleAddClientEmail = async () => {
    if (!newClientEmail || !newClientEmail.trim()) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newClientEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSavingEmail(true);
    try {
      const clientRef = doc(db, "clients", invoice.contactId);
      await updateDoc(clientRef, {
        email: newClientEmail.trim()
      });

      // Update local state
      if (clientDetails) {
        clientDetails.email = newClientEmail.trim();
        setClientDetails({ ...clientDetails });
      }

      toast.success("Email added successfully");
      setShowAddEmailModal(false);
      setNewClientEmail("");

      // Now proceed to send invoice confirmation modal
      setShowSendInvoiceModal(true);
    } catch (error) {
      console.error("Error adding email to client:", error);
      toast.error("Failed to add email");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSendInvoice = async () => {
    setSendingEmail(true);

    try {
      // Generate PDF and convert to base64
      const pdf = generatePDF();
      const pdfBase64 = pdf.output('datauristring').split(',')[1]; // Get base64 without data:application/pdf;base64, prefix

      const response = await fetch('https://jurident-feedback-backend-596718606544.us-central1.run.app/send-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientEmail: clientDetails.email,
          clientName: clientDetails.salutation
            ? `${clientDetails.salutation} ${clientDetails.name}`
            : clientDetails.name,
          lawyerName: lawyerDetails?.name || globalUser?.displayName,
          lawyerEmail: lawyerDetails?.email || globalUser?.email,
          invoiceNumber: invoice.invoiceNumber,
          invoiceAmount: invoice.totalAmount,
          invoiceCurrency: getCurrencySymbol(invoice.currency),
          caseNumber: invoice.resourceType === "case" && invoice.selectedCase?.caseNo ? invoice.selectedCase.caseNo : null,
          caseName: invoice.resourceType === "case" && invoice.resourceName ? invoice.resourceName : null,
          pdfBase64: pdfBase64
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Invoice sent successfully to ${clientDetails.email}`);
        setShowSendInvoiceModal(false);
      } else {
        throw new Error(data.error || 'Failed to send invoice');
      }

    } catch (error) {
      console.error("Error sending invoice:", error);
      toast.error(error.message || "Failed to send invoice");
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C99F4A] mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-6xl w-full my-6">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Invoice Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Invoice Details Content */}
        <div className="p-8">
          {/* Top Section - Two Columns */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Raised On:</strong> {formatDate(invoice.raisedOn)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Customer:</strong> {invoice.contactName}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Raised By:</strong> {invoice.raisedBy}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Due On:</strong> {formatDate(invoice.dueDate)}{" "}
                  {!readOnly && (
                  <button
                    onClick={() => setShowDueDateModal(true)}
                    className="text-blue-400 hover:text-blue-300 text-xs ml-2"
                  >
                    Edit
                  </button>
                  )}
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Listing:</strong> Case Status Page - {formatDate(invoice.dueDate)}
                </p>
              </div>
              {invoice.resourceType === "case" && invoice.selectedCase && (
                <div>
                  <p className="text-sm text-gray-400">
                    <strong className="text-white">Cases:</strong> {invoice.selectedCase.court || "Court"},{" "}
                    {invoice.selectedCase.location || "Location"} - {invoice.resourceName}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Point of Contact Section */}
          <div className="bg-[#0f0f23] border border-gray-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-white">Point of Contact</h3>
              {!readOnly && (
              <button
                onClick={() => setShowContactModal(true)}
                className="px-3 py-1 bg-[#0f0f23] border border-gray-700 rounded text-sm text-white hover:bg-gray-700 transition-colors"
              >
                Change
              </button>
              )}
            </div>
            {clientDetails && (
              <div className="text-sm text-gray-400 space-y-1">
                <p className="font-semibold text-white">
                  {clientDetails.salutation
                    ? `${clientDetails.salutation} ${clientDetails.name}`
                    : clientDetails.name}
                </p>
                {clientDetails.company && (
                  <p className="text-gray-300">{clientDetails.company}</p>
                )}
                {clientDetails.email && <p>{clientDetails.email}</p>}
                {clientDetails.contact && <p>{clientDetails.contact}</p>}
                {clientDetails.primaryAddress && (
                  <p>{formatAddress(clientDetails.primaryAddress)}</p>
                )}
              </div>
            )}
          </div>

          {/* Invoice Info Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-400">
                <strong className="text-white">Invoice:</strong> {invoice.invoiceNumber}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400">
                <strong className="text-white">Status:</strong>{" "}
                <span className="text-cyan-400 capitalize">{invoice.status}</span>
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400">
                <strong className="text-white">Total Amount:</strong> {formatAmount(invoice.totalAmount, invoice.currency)}
              </p>
            </div>
          </div>

          {/* Particulars Section */}
          {invoice.resourceType === "case" && invoice.selectedCase && (
            <div className="bg-[#0f0f23] border border-gray-700 rounded-lg mb-6">
              <div
                className="bg-gray-700 p-3 flex items-center justify-between cursor-pointer hover:bg-gray-600 transition-colors"
                onClick={() => setShowParticulars(!showParticulars)}
              >
                <h3 className="font-semibold text-white">
                  {invoice.selectedCase.court || "Court"},{" "}
                  {invoice.selectedCase.location || "Location"} - {invoice.resourceName}
                </h3>
                {showParticulars ? <ChevronUp size={20} className="text-white" /> : <ChevronDown size={20} className="text-white" />}
              </div>

              {showParticulars && (
                <div className="p-4">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-2 text-sm font-semibold text-gray-300">
                          Particulars
                        </th>
                        <th className="text-right py-2 text-sm font-semibold text-gray-300">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.particulars &&
                        invoice.particulars.map((particular, index) => (
                          <tr key={index} className="border-b border-gray-700">
                            <td className="py-3 text-sm text-gray-400">{particular.description}</td>
                            <td className="py-3 text-sm text-right text-gray-400">
                              {formatAmount(particular.amount, invoice.currency)}
                            </td>
                          </tr>
                        ))}
                      {invoice.selectedExpenses &&
                        invoice.selectedExpenses.length > 0 &&
                        invoice.selectedCase.expenses &&
                        invoice.selectedExpenses.map((expenseId) => {
                          const expense = invoice.selectedCase.expenses.find(
                            (e) => e.id === expenseId
                          );
                          if (!expense) return null;
                          return (
                            <tr key={expenseId} className="border-b border-gray-700">
                              <td className="py-3 text-sm text-gray-400">
                                {expense.description || expense.type}
                              </td>
                              <td className="py-3 text-sm text-right text-gray-400">
                                {formatAmount(expense.amount, invoice.currency)}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>

                  <div className="flex justify-end mt-4 pt-4 border-t border-gray-700">
                    <p className="text-sm font-semibold text-white">
                      Resource Total: {formatAmount(invoice.totalAmount, invoice.currency)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* For non-case invoices */}
          {invoice.resourceType !== "case" && (
            <div className="bg-[#0f0f23] border border-gray-700 rounded-lg mb-6 p-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-sm font-semibold text-gray-300">
                      Particulars
                    </th>
                    <th className="text-right py-2 text-sm font-semibold text-gray-300">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.particulars &&
                    invoice.particulars.map((particular, index) => (
                      <tr key={index} className="border-b border-gray-700">
                        <td className="py-3 text-sm text-gray-400">{particular.description}</td>
                        <td className="py-3 text-sm text-right text-gray-400">
                          {formatAmount(particular.amount, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              <div className="flex justify-end mt-4 pt-4 border-t border-gray-700">
                <p className="text-sm font-semibold text-white">
                  Total: {formatAmount(invoice.totalAmount, invoice.currency)}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!readOnly ? (
          <div className="flex items-center gap-3 mb-6">
            {invoice.status !== "paid" ? (
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                <CheckCircle2 size={16} />
                {markingPaid ? "Marking..." : "Mark as Paid"}
              </button>
            ) : (
              <button
                onClick={handleMarkUnpaid}
                disabled={markingPaid}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                <CheckCircle2 size={16} />
                {markingPaid ? "Marking..." : "Mark Unpaid"}
              </button>
            )}
            <button
              onClick={handleSendInvoiceClick}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
              title={!clientDetails?.email ? "Click to add client email and send invoice" : "Send invoice to client"}
            >
              <Send size={16} />
              Send Invoice to Client
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-[#0f0f23] border border-gray-700 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download size={16} />
              Download
            </button>
            <button
              onClick={handleOpenAttachExpensesModal}
              className="px-4 py-2 bg-[#0f0f23] border border-gray-700 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Attach Expense
            </button>
            <div className="relative">
              <button
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                className="px-4 py-2 bg-[#0f0f23] border border-gray-700 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                Actions
                <ChevronDown size={16} />
              </button>

              {/* Actions Dropdown */}
              {showActionsDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-[#1a1a2e] border border-gray-700 rounded-lg shadow-xl z-50">
                  <button
                    onClick={handleEditInvoice}
                    className="w-full px-4 py-3 text-left text-white hover:bg-[#252540] transition-colors flex items-center gap-2 rounded-t-lg"
                  >
                    <Edit2 size={16} />
                    Edit Invoice
                  </button>
                  <button
                    onClick={handleViewPDF}
                    className="w-full px-4 py-3 text-left text-white hover:bg-[#252540] transition-colors flex items-center gap-2"
                  >
                    <Download size={16} />
                    View PDF
                  </button>
                  <button
                    onClick={handleDeleteInvoice}
                    className="w-full px-4 py-3 text-left text-red-400 hover:bg-red-900/20 transition-colors flex items-center gap-2 rounded-b-lg"
                  >
                    <Trash2 size={16} />
                    Delete Invoice
                  </button>
                </div>
              )}
            </div>
          </div>
          ) : (
          <div className="flex items-center gap-3 mb-6">
            {invoice.status !== "paid" ? (
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                <CheckCircle2 size={16} />
                {markingPaid ? "Marking..." : "Mark as Paid"}
              </button>
            ) : (
              <button
                onClick={handleMarkUnpaid}
                disabled={markingPaid}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                <CheckCircle2 size={16} />
                {markingPaid ? "Marking..." : "Mark Unpaid"}
              </button>
            )}
            <div className="px-4 py-2 bg-gray-700/50 text-gray-300 rounded-lg text-sm font-medium">
              View Only - Shared by {invoice.sharedByName || invoice.raisedBy}
            </div>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-[#0f0f23] border border-gray-700 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download size={16} />
              Download
            </button>
          </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-700 mb-6">
            <div className="flex gap-6">
              {["payments", "creditNotes", "notes", "timeline"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-2 text-sm capitalize ${
                    activeTab === tab
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  {tab === "creditNotes"
                    ? "Credit Notes"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content - Fixed height with scroll */}
          <div className="max-h-96 overflow-y-auto px-6 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {activeTab === "payments" && (
            <PaymentsTab
              invoice={invoice}
              onCapturePayment={readOnly ? undefined : () => setShowCapturePaymentModal(true)}
              onEditPayment={readOnly ? undefined : handleEditPayment}
              onDeletePayment={readOnly ? undefined : handleDeletePayment}
            />
          )}

          {activeTab === "creditNotes" && (
            <CreditNotesTab
              invoice={invoice}
              onAddCreditNote={readOnly ? undefined : () => setShowCreditNoteModal(true)}
              onEditCreditNote={readOnly ? undefined : (creditNote) => {
                setEditingCreditNote(creditNote);
                setCreditNoteData({
                  amount: creditNote.amount.toString(),
                  date: creditNote.date,
                  reason: creditNote.reason,
                  reference: creditNote.reference || "",
                  notes: creditNote.notes || ""
                });
                setShowCreditNoteModal(true);
              }}
              onDeleteCreditNote={readOnly ? undefined : handleDeleteCreditNote}
            />
          )}

          {activeTab === "notes" && (
            <NotesTab
              invoice={invoice}
              newNoteText={readOnly ? undefined : newNoteText}
              setNewNoteText={readOnly ? undefined : setNewNoteText}
              savingNote={readOnly ? undefined : savingNote}
              onAddNote={readOnly ? undefined : handleAddNote}
            />
          )}

          {activeTab === "timeline" && (
            <TimelineTab
              invoice={invoice}
              onViewTimelineEvent={(event) => {
                setSelectedTimelineEvent(event);
                setShowTimelineDetailModal(true);
              }}
            />
          )}
          </div>
        </div>
      </div>

      {/* Contact Change Modal */}
      <ContactChangeModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        allClients={allClients}
        selectedContactId={selectedContactId}
        setSelectedContactId={setSelectedContactId}
        onConfirm={handleContactChange}
      />

      {/* Due Date Edit Modal */}
      <DueDateModal
        isOpen={showDueDateModal}
        onClose={() => setShowDueDateModal(false)}
        dueDate={newDueDate}
        setDueDate={setNewDueDate}
        onConfirm={handleDueDateChange}
      />

      {/* Capture Payment Modal */}
      <CapturePaymentModal
        isOpen={showCapturePaymentModal}
        onClose={() => setShowCapturePaymentModal(false)}
        invoice={invoice}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        editingPayment={editingPayment}
        setEditingPayment={setEditingPayment}
        onSubmit={handleCapturePayment}
      />

      {/* Credit Note Modal */}
      <CreditNoteModal
        isOpen={showCreditNoteModal}
        onClose={() => setShowCreditNoteModal(false)}
        invoice={invoice}
        creditNoteData={creditNoteData}
        setCreditNoteData={setCreditNoteData}
        editingCreditNote={editingCreditNote}
        setEditingCreditNote={setEditingCreditNote}
        onSubmit={handleAddCreditNote}
      />

      {/* Send Invoice Confirmation Modal */}
      <SendInvoiceModal
        isOpen={showSendInvoiceModal}
        onClose={() => setShowSendInvoiceModal(false)}
        invoice={invoice}
        clientDetails={clientDetails}
        sendingEmail={sendingEmail}
        onSend={handleSendInvoice}
      />

      {/* Timeline Event Detail Modal */}
      <TimelineDetailModal
        isOpen={showTimelineDetailModal}
        onClose={() => {
          setShowTimelineDetailModal(false);
          setSelectedTimelineEvent(null);
        }}
        selectedTimelineEvent={selectedTimelineEvent}
      />

      {/* Attach Expenses Modal */}
      <AttachExpensesModal
        isOpen={showAttachExpensesModal}
        onClose={() => setShowAttachExpensesModal(false)}
        invoice={invoice}
        caseExpenses={caseExpenses}
        selectedExpenses={selectedExpenses}
        customExpenses={customExpenses}
        attachingExpenses={attachingExpenses}
        handleToggleExpense={handleToggleExpense}
        addCustomExpense={addCustomExpense}
        removeCustomExpense={removeCustomExpense}
        updateCustomExpense={updateCustomExpense}
        onSubmit={handleAttachExpenses}
      />

      {/* View PDF Modal */}
      <ViewPDFModal
        isOpen={showViewPdfModal}
        onClose={() => setShowViewPdfModal(false)}
        invoice={invoice}
        pdfDataUrl={pdfDataUrl}
        setPdfDataUrl={setPdfDataUrl}
        onDownload={handleDownloadPDF}
      />

      {/* Add Email Modal */}
      {showAddEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Add Client Email</h3>
              <button
                onClick={() => {
                  setShowAddEmailModal(false);
                  setNewClientEmail("");
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-400 mb-4">
                The client <strong className="text-white">{clientDetails?.name || invoice.contactName}</strong> does not have an email address.
              </p>
              <p className="text-gray-400 mb-4">
                Please add an email address to send the invoice:
              </p>
              <input
                type="email"
                value={newClientEmail}
                onChange={(e) => setNewClientEmail(e.target.value)}
                placeholder="client@example.com"
                className="w-full bg-[#0f0f23] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#C99F4A]"
                disabled={savingEmail}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddEmailModal(false);
                  setNewClientEmail("");
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={savingEmail}
              >
                Cancel
              </button>
              <button
                onClick={handleAddClientEmail}
                className="flex-1 px-4 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={savingEmail}
              >
                {savingEmail ? "Saving..." : "Save & Send Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceDetailsView;
